import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Runs a page action requested from the sidebar via a `?action=<key>` query param.
 *
 * The sidebar dropdown items navigate to a page with e.g. `?action=create`; this
 * hook reads that param, invokes the matching handler (opening the same modal /
 * running the same export the in-page button would), then strips the param from
 * the URL so a refresh or back-navigation doesn't retrigger it.
 *
 * @param {Object<string, Function>} handlers - map of action key -> handler
 */
export default function useSidebarAction(handlers) {
  const location = useLocation();
  const navigate = useNavigate();
  // Keep the latest handlers without re-running the effect on every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");
    if (!action) return;

    const run = handlersRef.current?.[action];

    // Clear the param first so the action fires exactly once.
    params.delete("action");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true }
    );

    if (typeof run === "function") run();
  }, [location.pathname, location.search, navigate]);
}
